import { useEffect, useState } from 'react';
import { bundleService } from '../../../services/bundleService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCube,
  faPlus,
  faEdit,
  faCheckCircle,
  faTimesCircle,
  faUsers,
  faHistory,
  faSearch,
  faNairaSign,
  faCalendarAlt,
  faLayerGroup,
  faSave,
  faTimes,
  faTrash,
  faUserTag,
  faClock,
  faChartLine,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useUIStore } from '../../../store/uiStore';

export default function ManageBundles() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { pushToast } = useUIStore();
  
  const [form, setForm] = useState({
    name: '',
    price: '',
    usageLimit: '',
    durationDays: '',
    userType: 'normal',
    category: 'local',
    isActive: true
  });
  
  const [editingId, setEditingId] = useState(null);
  const [assignForm, setAssignForm] = useState({
    userId: '',
    bundleId: '',
    startDate: ''
  });
  
  const [userAssignments, setUserAssignments] = useState([]);
  const [userUsage, setUserUsage] = useState([]);
  const [loadingUserData, setLoadingUserData] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  async function refresh() {
    setLoading(true);
    try {
      const data = await bundleService.list(token);
      setBundles(data?.bundles || []);
      setError('');
    } catch (e) {
      setError(e?.message || 'Failed to load bundles');
      pushToast('Failed to load bundles', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = {
        name: form.name.trim(),
        price: Number(form.price),
        usageLimit: form.usageLimit ? Number(form.usageLimit) : undefined,
        durationDays: form.durationDays ? Number(form.durationDays) : undefined,
        userType: form.userType,
        category: form.category,
        isActive: !!form.isActive
      };
      
      if (editingId) {
        await bundleService.update(editingId, payload, token);
        pushToast('Bundle updated successfully', 'success');
      } else {
        await bundleService.create(payload, token);
        pushToast('Bundle created successfully', 'success');
      }
      
      setForm({ name: '', price: '', usageLimit: '', durationDays: '', userType: 'normal', category: 'local', isActive: true });
      setEditingId(null);
      await refresh();
    } catch (e) {
      setError(e?.message || 'Save failed');
      pushToast(e?.message || 'Save failed', 'error');
    }
  }

  function edit(b) {
    setEditingId(b._id);
    setForm({
      name: b.name,
      price: String(b.price),
      usageLimit: typeof b.usageLimit === 'number' ? String(b.usageLimit) : '',
      durationDays: typeof b.durationDays === 'number' ? String(b.durationDays) : '',
      userType: b.userType,
      category: b.category || 'local',
      isActive: !!b.isActive
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function toggleActive(b) {
    try {
      await bundleService.setActive(b._id, !b.isActive, token);
      pushToast(`Bundle ${b.isActive ? 'deactivated' : 'activated'}`, 'info');
      await refresh();
    } catch (e) {
      setError(e?.message || 'Update status failed');
      pushToast('Update status failed', 'error');
    }
  }

  async function assign(e) {
    e.preventDefault();
    try {
      const payload = {
        userId: assignForm.userId.trim(),
        bundleId: assignForm.bundleId,
        startDate: assignForm.startDate || undefined
      };
      await bundleService.assign(payload, token);
      pushToast('Bundle assigned successfully', 'success');
      
      // refresh user data
      await loadUserData();
      setError('');
    } catch (e) {
      setError(e?.message || 'Assignment failed');
      pushToast(e?.message || 'Assignment failed', 'error');
    }
  }

  async function loadUserData() {
    if (!assignForm.userId) {
      pushToast('Please enter a User ID', 'warning');
      return;
    }
    setLoadingUserData(true);
    try {
      const [ass, usage] = await Promise.all([
        bundleService.assignmentsForUser(assignForm.userId, token),
        bundleService.usageForUser(assignForm.userId, token)
      ]);
      setUserAssignments(ass?.assignments || []);
      setUserUsage(usage?.usage || []);
      if (ass?.assignments?.length === 0 && usage?.usage?.length === 0) {
        pushToast('No data found for this user', 'info');
      }
    } catch (e) {
      setError(e?.message || 'Failed to load user data');
      pushToast('Failed to load user data', 'error');
    } finally {
      setLoadingUserData(false);
    }
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header section */}
      <div className="bg-gradient-to-r from-red-600 to-black rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mr-4">
              <FontAwesomeIcon icon={faCube} className="text-2xl" />
            </div>
            <div>
              <h2 className="text-3xl font-bold">Manage Bundles</h2>
              <p className="text-red-100 opacity-80">Create and manage shipment bundle packages for users and vendors</p>
            </div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-red-500/10 rounded-full -ml-24 -mb-24 blur-2xl"></div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl flex items-center shadow-sm">
          <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 mr-3 text-lg" />
          <p className="text-red-700 font-medium">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden sticky top-6">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={editingId ? faEdit : faPlus} className="mr-2 text-red-600" />
                {editingId ? 'Edit Bundle' : 'Create New Bundle'}
              </h3>
              {editingId && (
                <button 
                  onClick={() => { setEditingId(null); setForm({ name: '', price: '', usageLimit: '', durationDays: '', userType: 'normal', category: 'local', isActive: true }); }}
                  className="text-xs text-gray-400 hover:text-red-600 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" /> Cancel
                </button>
              )}
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Bundle Name</label>
                <div className="relative">
                  <input 
                    value={form.name} 
                    onChange={e => setForm({ ...form, name: e.target.value })} 
                    className="w-full bg-gray-50 border-0 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-red-500 transition-all" 
                    placeholder="e.g. Lagos Express 10"
                    required 
                  />
                  <FontAwesomeIcon icon={faCube} className="absolute left-3 top-3.5 text-gray-300" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Price (NGN)</label>
                  <div className="relative">
                    <input 
                      type="number" 
                      min="0" 
                      value={form.price} 
                      onChange={e => setForm({ ...form, price: e.target.value })} 
                      onWheel={e => e.target.blur()} 
                      className="w-full bg-gray-50 border-0 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-red-500 transition-all" 
                      placeholder="0"
                      required 
                    />
                    <FontAwesomeIcon icon={faNairaSign} className="absolute left-3 top-3.5 text-gray-300" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Usage Limit</label>
                  <div className="relative">
                    <input 
                      type="number" 
                      min="0" 
                      value={form.usageLimit} 
                      onChange={e => setForm({ ...form, usageLimit: e.target.value })} 
                      onWheel={e => e.target.blur()} 
                      className="w-full bg-gray-50 border-0 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-red-500 transition-all" 
                      placeholder="Infinite" 
                    />
                    <FontAwesomeIcon icon={faChartLine} className="absolute left-3 top-3.5 text-gray-300" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Duration (Days)</label>
                  <div className="relative">
                    <input 
                      type="number" 
                      min="0" 
                      value={form.durationDays} 
                      onChange={e => setForm({ ...form, durationDays: e.target.value })} 
                      onWheel={e => e.target.blur()} 
                      className="w-full bg-gray-50 border-0 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-red-500 transition-all" 
                      placeholder="No expiry" 
                    />
                    <FontAwesomeIcon icon={faCalendarAlt} className="absolute left-3 top-3.5 text-gray-300" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">User Type</label>
                  <div className="relative">
                    <select 
                      value={form.userType} 
                      onChange={e => setForm({ ...form, userType: e.target.value })} 
                      className="w-full bg-gray-50 border-0 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-red-500 transition-all appearance-none"
                    >
                      <option value="normal">Normal User</option>
                      <option value="vendor">Vendor</option>
                    </select>
                    <FontAwesomeIcon icon={faUserTag} className="absolute left-3 top-3.5 text-gray-300" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Category</label>
                <div className="relative">
                  <select 
                    value={form.category} 
                    onChange={e => setForm({ ...form, category: e.target.value })} 
                    className="w-full bg-gray-50 border-0 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-red-500 transition-all appearance-none"
                  >
                    <option value="local">Local Delivery</option>
                    <option value="interstate">Interstate Delivery</option>
                  </select>
                  <FontAwesomeIcon icon={faLayerGroup} className="absolute left-3 top-3.5 text-gray-300" />
                </div>
              </div>

              <div className="flex items-center p-3 bg-gray-50 rounded-xl">
                <label className="flex items-center cursor-pointer w-full">
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={form.isActive} 
                      onChange={e => setForm({ ...form, isActive: e.target.checked })} 
                      className="sr-only" 
                    />
                    <div className={`block w-10 h-6 rounded-full transition-colors ${form.isActive ? 'bg-red-600' : 'bg-gray-300'}`}></div>
                    <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${form.isActive ? 'translate-x-4' : ''}`}></div>
                  </div>
                  <span className="ml-3 text-sm font-bold text-gray-700">Active and Purchaseable</span>
                </label>
              </div>

              <button 
                type="submit" 
                className="w-full bg-red-600 text-white font-bold py-4 rounded-2xl shadow-lg shadow-red-200 hover:bg-red-700 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center"
              >
                <FontAwesomeIcon icon={editingId ? faSave : faPlus} className="mr-2" />
                {editingId ? 'Update Bundle' : 'Add Bundle'}
              </button>
            </form>
          </div>
        </div>

        {/* Right Column: List and Assignments */}
        <div className="lg:col-span-2 space-y-8">
          {/* Bundles Table */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
              <h3 className="font-bold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faCube} className="mr-2 text-red-600" />
                Active Bundles
              </h3>
              <span className="px-3 py-1 bg-white rounded-full text-xs font-bold text-gray-500 border border-gray-100 shadow-sm">
                {bundles.length} total
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Bundle Details</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Pricing</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Type / Cat</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-2"></div>
                          <p className="text-gray-400 text-sm">Loading bundles...</p>
                        </div>
                      </td>
                    </tr>
                  ) : bundles.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-400">
                        <div className="flex flex-col items-center">
                          <FontAwesomeIcon icon={faCube} className="text-3xl mb-3 opacity-20" />
                          <p>No bundles created yet</p>
                        </div>
                      </td>
                    </tr>
                  ) : bundles.map(b => (
                    <tr key={b._id} className="hover:bg-gray-50 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 ${b.isActive ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-400'}`}>
                            <FontAwesomeIcon icon={faCube} />
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{b.name}</p>
                            <div className="flex items-center space-x-2 mt-0.5">
                              <span className="text-[10px] text-gray-400 flex items-center">
                                <FontAwesomeIcon icon={faChartLine} className="mr-1" />
                                {typeof b.usageLimit === 'number' ? `${b.usageLimit} uses` : 'Unlimited'}
                              </span>
                              <span className="text-gray-300">•</span>
                              <span className="text-[10px] text-gray-400 flex items-center">
                                <FontAwesomeIcon icon={faClock} className="mr-1" />
                                {typeof b.durationDays === 'number' ? `${b.durationDays} days` : 'Permanent'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900">₦{Number(b.price).toLocaleString()}</p>
                        <p className={`text-[10px] font-bold uppercase ${b.isActive ? 'text-green-500' : 'text-gray-400'}`}>
                          {b.isActive ? 'Active' : 'Inactive'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-1">
                          <span className={`w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${b.userType === 'vendor' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {b.userType}
                          </span>
                          <span className="text-[10px] text-gray-400 font-medium capitalize">
                            {b.category || 'Local'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => edit(b)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit bundle"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button 
                            onClick={() => toggleActive(b)}
                            className={`p-2 rounded-lg transition-colors ${b.isActive ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
                            title={b.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <FontAwesomeIcon icon={b.isActive ? faTimesCircle : faCheckCircle} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* User Assignment Section */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-50 bg-gray-50/50">
              <h3 className="font-bold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faUsers} className="mr-2 text-red-600" />
                Assign Bundle to User
              </h3>
            </div>
            <div className="p-6">
              <form onSubmit={assign} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">User ID</label>
                  <div className="relative">
                    <input 
                      value={assignForm.userId} 
                      onChange={e => setAssignForm({ ...assignForm, userId: e.target.value })} 
                      className="w-full bg-gray-50 border-0 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-red-500 transition-all" 
                      placeholder="User MongoID"
                      required 
                    />
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3.5 text-gray-300" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Select Bundle</label>
                  <div className="relative">
                    <select 
                      value={assignForm.bundleId} 
                      onChange={e => setAssignForm({ ...assignForm, bundleId: e.target.value })} 
                      className="w-full bg-gray-50 border-0 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-red-500 transition-all appearance-none"
                      required
                    >
                      <option value="">Select a bundle...</option>
                      {bundles.filter(b => b.isActive).map(b => (
                        <option key={b._id} value={b._id}>{b.name} (₦{b.price.toLocaleString()})</option>
                      ))}
                    </select>
                    <FontAwesomeIcon icon={faCube} className="absolute left-3 top-3.5 text-gray-300" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Start Date</label>
                  <div className="relative">
                    <input 
                      type="date" 
                      value={assignForm.startDate} 
                      onChange={e => setAssignForm({ ...assignForm, startDate: e.target.value })} 
                      className="w-full bg-gray-50 border-0 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-red-500 transition-all" 
                    />
                    <FontAwesomeIcon icon={faCalendarAlt} className="absolute left-3 top-3.5 text-gray-300" />
                  </div>
                </div>
                <div className="md:col-span-3 flex space-x-3">
                  <button 
                    type="submit" 
                    className="flex-1 bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700 transition-all shadow-lg shadow-green-100 flex items-center justify-center"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" /> Assign Now
                  </button>
                  <button 
                    type="button" 
                    onClick={loadUserData}
                    disabled={loadingUserData}
                    className="flex-1 bg-gray-900 text-white font-bold py-3 rounded-xl hover:bg-black transition-all shadow-lg shadow-gray-200 flex items-center justify-center disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={loadingUserData ? faHistory : faSearch} className={`mr-2 ${loadingUserData ? 'animate-spin' : ''}`} />
                    {loadingUserData ? 'Loading...' : 'Check User Activity'}
                  </button>
                </div>
              </form>

              {/* User Data Tabs/Panels */}
              {(userAssignments.length > 0 || userUsage.length > 0) && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 animate-fadeIn">
                  {/* Current Assignments */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-900 flex items-center text-sm">
                      <FontAwesomeIcon icon={faUsers} className="mr-2 text-blue-600" />
                      Active Assignments
                    </h4>
                    <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-gray-100/50">
                            <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase">Bundle</th>
                            <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase">Left</th>
                            <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase">Expiry</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {userAssignments.map(a => {
                            const remaining = typeof a.usageLimit === 'number' ? Math.max(0, a.usageLimit - (a.shipmentsUsed || 0)) : null;
                            const isExpired = a.status === 'expired' || (a.expiryDate && new Date(a.expiryDate) < new Date());
                            return (
                              <tr key={a._id} className="hover:bg-white transition-colors">
                                <td className="px-4 py-3 font-bold text-gray-800 text-xs">{a.bundleName}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-tight ${
                                    a.status === 'active' ? 'bg-green-100 text-green-700' : 
                                    a.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {a.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-600 font-medium">
                                  {remaining !== null ? `${remaining} / ${a.usageLimit}` : '∞'}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500">
                                  {a.expiryDate ? new Date(a.expiryDate).toLocaleDateString() : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Usage History */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-900 flex items-center text-sm">
                      <FontAwesomeIcon icon={faHistory} className="mr-2 text-purple-600" />
                      Recent Usage
                    </h4>
                    <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-gray-100/50">
                            <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase">Tracking</th>
                            <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-[10px] font-bold text-gray-500 uppercase">Note</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {userUsage.length === 0 ? (
                            <tr><td colSpan="3" className="px-4 py-8 text-center text-gray-400 text-xs italic">No usage history found</td></tr>
                          ) : userUsage.slice(0, 5).map(u => (
                            <tr key={u._id} className="hover:bg-white transition-colors">
                              <td className="px-4 py-3 font-mono text-xs text-gray-800">{u.trackingCode || '—'}</td>
                              <td className="px-4 py-3 text-[10px] text-gray-500">
                                {new Date(u.usedAt).toLocaleDateString()}<br/>
                                {new Date(u.usedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="px-4 py-3 text-[10px] text-gray-400 max-w-[100px] truncate" title={u.note}>
                                {u.note || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tips / Info Card */}
          <div className="bg-blue-50 rounded-3xl p-6 border border-blue-100 flex items-start space-x-4">
            <div className="w-10 h-10 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-bold text-blue-900 text-sm mb-1">Bundle Management Pro-tips</h4>
              <ul className="text-xs text-blue-700 space-y-1 opacity-80">
                <li>• Deactivating a bundle prevents new purchases but keeps existing ones active.</li>
                <li>• Leave Usage Limit empty for unlimited shipments within the duration.</li>
                <li>• Assigning a bundle manually is useful for corporate gifts or manual payments.</li>
                <li>• Usage is tracked automatically when a user books a shipment using an active bundle.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
